import React from "react";
import { Box, TextField, Button, IconButton, Typography, InputAdornment, Avatar } from "@mui/material";
import { PhotoLibrary, VideoLibrary, AttachFile, RemoveCircle, PictureAsPdf, InsertDriveFile, InsertPhoto, VideoFile, PhotoLibraryOutlined, VideoLibraryOutlined, AttachFileOutlined, AttachFileRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import SendIcon from '@mui/icons-material/Send';
import defaultProfilePic from "@/assets/default-icon.jpg";


function InputArea({
  message,
  setMessage,
  handleSend,
  handleFileChange,
  handleRemoveFile,
  filePreviews,
  loading,
  onKeyPress, // New prop for handling key press events
  onChange,  // New prop for handling change events
}) {
  const customerData = useSelector((state) => state.auth.auth);

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) {
      return <InsertPhoto />;
    } else if (fileType.startsWith('video/')) {
      return <VideoFile />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdf />;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || fileType === 'application/vnd.ms-excel') {
      return <InsertDriveFile />;
    } else {
      return <AttachFile />;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        p: { xs: "0 12px", md: 0 }, // remove padding on web view
        gap: { xs: 1, md: 0.5 },
      }}
    >
      {/* Encryption Security Notice */}
      <Box
        sx={{
          // mb: { xs: 1, md: 1 },
          p: { xs: 0.5, sm: 0.5 },
          background:
            "linear-gradient(135deg, #e8f5e8 0%, #f0f8ff 100%)",
          borderRadius: { xs: 1.5, md: 0.5 }, // smaller radius for web
          border: "1px solid #4caf50",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            background:
              "linear-gradient(90deg, #4caf50, #2196f3, #4caf50)",
            backgroundSize: "200% 100%",
            animation: "shimmer 4s ease-in-out infinite",
          },
          "@keyframes shimmer": {
            "0%": { backgroundPosition: "-200% 0" },
            "100%": { backgroundPosition: "200% 0" },
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#2e7d32",
            fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.8rem" },
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            textAlign: "center",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              animation: "pulse 2s ease-in-out infinite",
            }}
          >
            🔐
          </Box>
          <span>All messages and files are end-to-end encrypted for your privacy</span>
        </Typography>
      </Box>

      {/* Message Input */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#f2f2f8",
          borderRadius: { xs: "4px", md: "3px" },
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: "6px", md: "6px" },
          minHeight: { xs: "32px", md: "36px" },
          maxHeight: { xs: "140px", md: "140px" },
          paddingTop: { xs: "4px", md: "4px" },
          paddingBottom: { xs: "4px", md: "4px" },
          // paddingLeft: { xs: "4px", md: "6px" },
          padding: "4px 1px !important",
        }}
      >
        <Avatar
          src={customerData?.profile_picture?.file_url}
          alt={`${customerData?.first_name || "User"} avatar`}
          sx={{
            bgcolor: "primary.main",
            width: { xs: "24px", md: "26px" },
            height: { xs: "24px", md: "26px" },
            alignSelf: "center",
            flexShrink: 0,
          }}
        />

        <TextField
          fullWidth
          placeholder="Type here..."
          multiline
          minRows={1}
          maxRows={4}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            onChange(e);
          }} 
          onKeyPress={onKeyPress}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: "4px" }}>
                <Button
                  variant="text"
                  onClick={handleSend}
                  disabled={loading}
                  startIcon={<SendIcon />}
                  sx={{
                    color: "#5e5acf",
                    fontWeight: 500,
                    fontSize: { xs: "11px", md: "12px" },
                    minWidth: { xs: "40px", md: "45px" },
                    height: { xs: "24px", md: "26px" },
                  }}
                >
                  {loading ? "..." : "Send"}
                </Button>
              </InputAdornment>
            ),
            sx: {
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-input": {
                overflow: "auto !important",
                maxHeight: "none",
              },
            },
          }}
          sx={{
            padding: "1px !important",
            backgroundColor: "transparent",
            color: "#8992a3",
            fontSize: { xs: "12px", md: "13px" },
            flex: 1,
            "& .MuiInputBase-root": {
              alignItems: "flex-start",
            },
            "& textarea": {
              overflowY: "auto !important",
              resize: "none",
            },
          }}
        />
      </Box>

      {/* File Upload + Preview (side by side on web) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "flex-start",
          alignItems: "center",
          gap: { xs: "8px", md: "10px" },
          mt: { xs: "4px", md: "2px" },
          // p: { xs: "4px 6px", md: 0 }, // remove padding web
          backgroundColor: "rgba(0, 0, 0, 0.03)",
          borderRadius: { xs: "5px", md: "3px" },
          border: "1px solid rgba(0, 0, 0, 0.1)",
          minHeight: { xs: "32px", md: "36px" },
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* File upload buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "6px", md: "8px" },
          }}
        >
          {[
            {
              id: "imageInput",
              icon: <PhotoLibraryOutlined />,
              label: "Image",
              accept: "image/*",
              color: "#4caf50",
            },
            {
              id: "videoInput",
              icon: <VideoLibraryOutlined />,
              label: "Video",
              accept: "video/*",
              color: "#ff9800",
            },
            {
              id: "fileInput",
              icon: <AttachFileRounded />,
              label: "File",
              accept: ".pdf,.doc,.docx,.xls,.xlsx,.txt",
              color: "#2196f3",
            },
          ].map((item) => (
            <label htmlFor={item.id} key={item.id} style={{ cursor: "pointer" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: "2px", md: "3px" },
                  color: "#666",
                  px: { xs: "4px", md: "6px" },
                  py: { xs: "3px", md: "3px" },
                  borderRadius: { xs: "4px", md: "2px" },
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  border: `1px solid ${item.color}30`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: `${item.color}15`,
                    color: item.color,
                    boxShadow: `0 1px 3px ${item.color}40`,
                  },
                }}
              >
                <IconButton
                  component="span"
                  sx={{
                    p: 0,
                    fontSize: { xs: "14px", md: "15px" },
                    color: "inherit",
                  }}
                >
                  {item.icon}
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.55rem", md: "0.6rem" },
                    fontWeight: 600,
                    display: { xs: "none", sm: "block" },
                    color: "inherit",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <input
                type="file"
                id={item.id}
                style={{ display: "none" }}
                accept={item.accept}
                multiple
                onChange={handleFileChange}
              />
            </label>
          ))}
        </Box>

        {/* File previews (side by side on desktop) */}
        {filePreviews.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: "6px", md: "8px" },
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            {filePreviews.map((preview, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  width: { xs: "60px", md: "65px" },
                  height: { xs: "60px", md: "65px" },
                }}
              >
                {preview.type.startsWith("image/") ? (
                  <img
                    src={preview.preview}
                    alt="preview"
                    style={{
                      width: "90%",
                      height: "70%",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  />
                ) : preview.type.startsWith("video/") ? (
                  <video
                    src={preview.preview}
                    controls
                    style={{
                      width: "90%",
                      height: "70%",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "90%",
                      height: "70%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      backgroundColor: "#f2f2f8",
                    }}
                  >
                    {getFileIcon(preview.type)}
                  </Box>
                )}
                <IconButton
                  onClick={() => handleRemoveFile(index)}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "-5px",
                    right: "-5px",
                    color: "red",
                    background: "white",
                    borderRadius: "50%",
                    // padding: "2px",
                  }}
                >
                  <RemoveCircle />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default InputArea;